import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';
import { apiClient } from '../../../../lib/api';
import type { EmailTemplate, GmailConnection } from '../types';

interface UseCampaignDataResult {
  templates: EmailTemplate[];
  gmailConnections: GmailConnection[];
  isLoading: boolean;
  error: string | null;
  setTemplates: Dispatch<SetStateAction<EmailTemplate[]>>;
  refreshTemplates: () => Promise<void>;
  getDefaultEmailAccount: () => GmailConnection | undefined;
}

/**
 * Hook for loading campaign data (templates and Gmail connections).
 * Handles initial data fetch and provides refresh capability.
 */
export function useCampaignData(): UseCampaignDataResult {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [gmailConnections, setGmailConnections] = useState<GmailConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load templates and connections on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [templatesRes, connectionsRes] = await Promise.all([
          apiClient.getEmailTemplates(),
          apiClient.getAllGmailConnections(),
        ]);

        setTemplates(templatesRes.templates || []);
        // Map connections to ensure token_status matches GmailConnection type
        const connections: GmailConnection[] = (connectionsRes.connections || []).map((conn: any) => ({
          id: conn.id,
          gmail_email: conn.gmail_email,
          is_primary: conn.is_primary,
          token_status: conn.token_status as 'active' | 'expired' | 'revoked',
        }));
        setGmailConnections(connections);
      } catch (err) {
        console.error('Failed to load campaign data:', err);
        setError('Failed to load campaign configuration');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Refresh templates list
  const refreshTemplates = useCallback(async () => {
    try {
      const response = await apiClient.getEmailTemplates();
      setTemplates(response.templates || []);
    } catch (err) {
      console.error('Failed to refresh templates:', err);
    }
  }, []);

  // Get default email account (primary or first active)
  const getDefaultEmailAccount = useCallback((): GmailConnection | undefined => {
    const primary = gmailConnections.find(
      (c) => c.is_primary && c.token_status === 'active'
    );
    return primary || gmailConnections.find((c) => c.token_status === 'active');
  }, [gmailConnections]);

  return {
    templates,
    gmailConnections,
    isLoading,
    error,
    setTemplates,
    refreshTemplates,
    getDefaultEmailAccount,
  };
}
